import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterPanel from '../../components/ui/FilterPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { fetchCampaigns, deleteCampaign } from '../../api/campaigns';
import { useUserAccess } from '../../context/UserAccessContext';
import { canManageCrmCampaigns } from '../../utils/crmAccess';

const Campaigns = () => {
  const navigate = useNavigate();
  const { user } = useUserAccess();
  const canManage = canManageCrmCampaigns(user);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const campaignsRes = await fetchCampaigns();
      setCampaigns(campaignsRes?.data?.campaigns ?? []);
    } catch (e) {
      console.error(e);
      setError('Impossible de charger les campagnes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const sortedCampaigns = useMemo(() => {
    return [...campaigns].sort((a, b) => {
      const da = a.status === 'draft' ? 0 : 1;
      const db = b.status === 'draft' ? 0 : 1;
      if (da !== db) return da - db;
      return String(b.updatedAt || b.createdAt || '').localeCompare(
        String(a.updatedAt || a.createdAt || ''),
      );
    });
  }, [campaigns]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette campagne ?')) return;
    setDeletingId(id);
    try {
      await deleteCampaign(id);
      setCampaigns((prev) => prev.filter((c) => c._id !== id));
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  const openCampaign = (campaign) => {
    navigate(`/campaigns/${campaign._id}/products`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Campagnes - CRM</title>
      </Helmet>
      <Header />
      <Breadcrumb />
      <FilterPanel />

      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Campagnes
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Créez une campagne, puis configurez produits, HubUP et emails dans la fiche.
              </p>
            </div>
            {canManage ? (
              <Button
                onClick={() => navigate('/campaigns/new')}
                iconName="Plus"
                iconPosition="left"
                size="lg"
              >
                Créer une campagne
              </Button>
            ) : null}
          </div>
        </div>

        {error && <p className="text-sm text-error mb-4">{error}</p>}
        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement...</p>
        ) : campaigns.length === 0 ? (
          <div className="bg-card rounded-lg p-12 text-center border border-border">
            <Icon name="Megaphone" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Aucune campagne</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Titre, description et tag — le reste se fait ensuite.
            </p>
            {canManage ? (
              <Button onClick={() => navigate('/campaigns/new')} iconName="Plus" iconPosition="left">
                Créer une campagne
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {sortedCampaigns.map((campaign) => {
              const isDraft = campaign.status === 'draft';
              return (
                <div
                  key={campaign._id}
                  className={`bg-card rounded-lg p-4 shadow-elevation-1 hover:shadow-elevation-2 transition-smooth border flex flex-col h-full cursor-pointer ${
                    isDraft ? 'border-dashed border-primary/40' : 'border-border'
                  }`}
                  onClick={() => openCampaign(campaign)}
                  onKeyDown={(e) => e.key === 'Enter' && openCampaign(campaign)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      {isDraft ? (
                        <span className="mb-1 inline-block text-xs font-medium rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                          Brouillon
                        </span>
                      ) : null}
                      <h3 className="text-base font-semibold text-foreground line-clamp-2">
                        {campaign.name}
                      </h3>
                    </div>
                    {canManage ? (
                      <div
                        className="flex items-center gap-1 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="p-1.5 rounded hover:bg-destructive/10 transition-smooth"
                          onClick={() => handleDelete(campaign._id)}
                          disabled={deletingId === campaign._id}
                          aria-label="Supprimer"
                        >
                          <Icon name="Trash2" size={14} className="text-error" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 flex-1 mb-3">
                    {campaign.description || 'Aucune description'}
                  </p>
                  <div className="pt-3 border-t border-border flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Package" size={12} />
                      {Array.isArray(campaign.products) ? campaign.products.length : 0} produit(s)
                    </span>
                    {campaign.hubupGroupId ? (
                      <span className="flex items-center gap-1">
                        <Icon name="Radio" size={12} />
                        HubUP
                      </span>
                    ) : null}
                    {Array.isArray(campaign.tags) && campaign.tags.length > 0 && (
                      <span className="flex items-center gap-1 flex-wrap">
                        {campaign.tags.map((t) => (
                          <span
                            key={typeof t === 'string' ? t : t._id}
                            className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                          >
                            {typeof t === 'object' && t?.name ? t.name : t}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  <div
                    className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-2 text-xs font-medium text-foreground transition-smooth hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => {
                        const tagNames = campaignTagNames(campaign);
                        const q = tagNames.map((n) => `tags=${encodeURIComponent(n)}`).join('&');
                        navigate(`/prospect-contact-directory?${q}`);
                      }}
                      disabled={campaignTagNames(campaign).length === 0}
                    >
                      <Icon name="Users" size={14} />
                      Prospects
                    </button>
                    <button
                      type="button"
                      className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-2 text-xs font-medium text-primary transition-smooth hover:bg-primary/10"
                      onClick={() => navigate(`/campaigns/${campaign._id}/email`)}
                    >
                      <Icon name="Mail" size={14} />
                      Email
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

    </div>
  );
};

function campaignTagNames(campaign) {
  return Array.isArray(campaign?.tags)
    ? campaign.tags
        .map((t) => (typeof t === 'object' && t?.name ? t.name : String(t || '')))
        .map((t) => t.trim())
        .filter(Boolean)
    : [];
}

export default Campaigns;
